<template>
  <div class="admin-home-page">
    <section class="admin-home-hero">
      <div class="label">
        <div>
          <span class="eyebrow">Administration</span>
          <h4>Admin Overview</h4>
        </div>
        <div class="hero-actions">
          <router-link class="btn-secondary" to="/users/audience">Audience detail</router-link>
          <button class="btn-refresh" :disabled="loading" @click="fetchAll()">
            {{ loading ? 'Refreshing...' : 'Refresh' }}
          </button>
        </div>
      </div>
      <p class="intro">
        Admin overview for audience growth, catalog engagement, Amazon intent, and analytics hygiene.
        Use the detail pages when you need to drill into sessions, products, or exclusions.
      </p>
    </section>

    <div v-if="error" class="alert">{{ error }}</div>
    <div v-if="notice" class="notice">{{ notice }}</div>

    <section class="kpi-grid">
      <article
        v-for="card in summaryCards"
        :key="card.key"
        class="kpi-card"
        :class="['tone-' + card.tone, { 'is-loading': card.loading }]"
      >
        <span class="kpi-label">{{ card.label }}</span>
        <strong>{{ card.loading ? '...' : card.value }}</strong>
        <small>{{ card.loading ? 'Loading...' : card.note }}</small>
      </article>
    </section>

    <section class="ops-grid">
      <article
        v-for="card in operationsCards"
        :key="card.key"
        class="op-card"
        :class="['tone-' + card.tone, { 'is-loading': card.loading }]"
      >
        <div>
          <span class="panel-kicker">{{ card.kicker }}</span>
          <h5>{{ card.label }}</h5>
        </div>
        <strong>{{ card.loading ? '...' : card.value }}</strong>
        <small>{{ card.loading ? 'Loading...' : card.note }}</small>
      </article>
    </section>

    <section class="dashboard-grid">
      <article class="panel panel-wide">
        <div class="panel-headline">
          <div>
            <span class="panel-kicker">{{ chartDays }} day trend</span>
            <h5>Audience activity</h5>
          </div>
          <div class="chart-head-tools">
            <div class="chart-window-toggle" aria-label="Audience chart window">
              <button
                v-for="days in chartDayOptions"
                :key="'chart-days-' + days"
                type="button"
                :class="{ active: chartDays === days }"
                :disabled="dailyLoading && chartDays === days"
                @click="setChartDays(days)"
              >{{ days }}d</button>
            </div>
            <div class="chart-legend">
              <span><i class="line-sessions"></i>Sessions</span>
              <span><i class="line-users"></i>Active users</span>
              <span><i class="line-views"></i>Page views</span>
              <span><i class="line-amazon"></i>Amazon clicks</span>
            </div>
          </div>
        </div>
        <div v-if="dailyLoading && !dailySeries.length" class="empty-panel chart-loading">Loading audience trend...</div>
        <div class="line-chart-wrap" v-else-if="dailySeries.length">
          <svg class="line-chart" viewBox="0 0 640 220" preserveAspectRatio="none" aria-label="Daily audience chart">
            <line v-for="y in chartGridLines" :key="'y-' + y" x1="0" :y1="y" x2="640" :y2="y"></line>
            <polyline :points="chartPath('sessions')" class="chart-line chart-line-sessions"></polyline>
            <polyline :points="chartPath('unique_users')" class="chart-line chart-line-users"></polyline>
            <polyline :points="chartPath('page_views')" class="chart-line chart-line-views"></polyline>
            <polyline :points="chartPath('amazon_clicks')" class="chart-line chart-line-amazon"></polyline>
            <rect
              v-for="hit in chartHoverRects"
              :key="hit.key"
              class="chart-hit"
              :x="hit.x"
              y="0"
              :width="hit.width"
              height="220"
            >
              <title>{{ hit.title }}</title>
            </rect>
          </svg>
          <div class="chart-axis">
            <span
              v-for="tick in chartAxisTicks"
              :key="tick.key"
              :style="{ left: tick.left + '%' }">
              {{ tick.label }}
            </span>
          </div>
        </div>
        <div v-else class="empty-panel">No daily rollup data yet.</div>
      </article>

      <article class="panel">
        <div class="panel-headline">
          <div>
            <span class="panel-kicker">Traffic mix</span>
            <h5>Top sources</h5>
          </div>
        </div>
        <div class="bar-list">
          <div v-for="item in topSources" :key="'source-' + item.label" class="bar-row">
            <span :title="item.label">{{ item.label }}</span>
            <div><i :style="{ width: item.percent + '%' }"></i></div>
            <strong>{{ item.count }}</strong>
          </div>
          <div v-if="trafficLoading && !topSources.length" class="mini-empty">Loading traffic sources...</div>
          <div v-else-if="!topSources.length" class="mini-empty">No source data.</div>
        </div>
      </article>

      <article class="panel">
        <div class="panel-headline">
          <div>
            <span class="panel-kicker">Devices</span>
            <h5>Visitor devices</h5>
          </div>
        </div>
        <div class="bar-list">
          <div v-for="item in topDevices" :key="'device-' + item.label" class="bar-row">
            <span>{{ item.label }}</span>
            <div><i :style="{ width: item.percent + '%' }"></i></div>
            <strong>{{ item.count }}</strong>
          </div>
          <div v-if="!topDevices.length" class="mini-empty">No device data.</div>
        </div>
      </article>

      <article class="panel">
        <div class="panel-headline">
          <div>
            <span class="panel-kicker">Content</span>
            <h5>Top pages</h5>
          </div>
        </div>
        <div class="rank-list">
          <div v-for="item in topPages" :key="'page-' + item.label" class="rank-row">
            <span :title="item.label">{{ item.label }}</span>
            <strong>{{ item.count }}</strong>
          </div>
          <div v-if="!topPages.length" class="mini-empty">No page data.</div>
        </div>
      </article>

      <article class="panel rollup-panel">
        <div class="panel-headline">
          <div>
            <span class="panel-kicker">Maintenance</span>
            <h5>Analytics rollup</h5>
          </div>
        </div>
        <p>Rebuild the daily analytics tables after importing or repairing raw session events.</p>
        <div class="rollup-actions">
          <label>
            <span>Days</span>
            <input type="number" min="1" max="90" v-model.number="rollupDays">
          </label>
          <button class="btn-refresh" :disabled="rollupBusy" @click="runRollup()">
            {{ rollupBusy ? 'Rebuilding...' : 'Rebuild analytics rollup' }}
          </button>
        </div>
      </article>
    </section>

    <section class="panel product-panel">
      <div class="panel-headline">
        <div>
          <span class="panel-kicker">Product intent</span>
          <h5>Popular products</h5>
        </div>
        <div class="product-head-actions">
          <span class="panel-note">Last {{ productDays }} days</span>
          <button
            v-if="products.length > 5"
            class="btn-secondary compact"
            type="button"
            @click="productsExpanded = !productsExpanded">
            {{ productsExpanded ? 'Show first 5' : 'View all' }}
          </button>
        </div>
      </div>
      <AdminTable
        :columns="productColumns"
        :rows="visibleProducts"
        :row-key="productRowKey"
        :loading="productsLoading"
        empty-text="No product activity yet."
      >
        <template v-slot:cell-product="{ row }">
          <span class="product-name" :title="productName(row)">{{ productName(row) }}</span>
          <span class="muted-sub mono">{{ productMeta(row) }}</span>
        </template>
        <template v-slot:cell-type="{ row }">
          <span class="type-pill">{{ partType(row) }}</span>
        </template>
        <template v-slot:cell-views="{ row }">
          <strong>{{ num(row.product_views) }}</strong>
        </template>
        <template v-slot:cell-actions="{ row }">
          <span class="event-stack">
            <b>{{ num(row.product_selects) }}</b> select
            <small>{{ num(row.product_adds) }} add / {{ num(row.product_removes) }} remove</small>
          </span>
        </template>
        <template v-slot:cell-amazon="{ row }">
          <strong class="amazon-count">{{ num(row.amazon_clicks) }}</strong>
        </template>
        <template v-slot:cell-total="{ row }">
          <strong>{{ num(row.total_events) }}</strong>
        </template>
      </AdminTable>
    </section>

    <section class="panel exclusions-panel">
      <div class="panel-headline">
        <div>
          <span class="panel-kicker">Analytics hygiene</span>
          <h5>Traffic exclusions</h5>
        </div>
      </div>

      <div class="exclusion-form">
        <select v-model="exclusionForm.scope">
          <option v-for="scope in exclusionScopes" :key="scope" :value="scope">{{ prettyLabel(scope) }}</option>
        </select>
        <input type="text" v-model="exclusionForm.value" placeholder="Value to exclude">
        <input type="text" v-model="exclusionForm.reason" placeholder="Reason">
        <button class="btn-refresh" :disabled="exclusionBusy || !exclusionForm.value" @click="createExclusion()">Add exclusion</button>
      </div>

      <AdminTable
        :columns="exclusionColumns"
        :rows="exclusions"
        :row-key="'id'"
        :loading="exclusionsLoading"
        empty-text="No exclusions configured."
      >
        <template v-slot:cell-scope="{ row }">
          <span class="type-pill">{{ prettyLabel(row.scope) }}</span>
        </template>
        <template v-slot:cell-value="{ row }">
          <span class="mono text-cell" :title="row.value">{{ row.value || '-' }}</span>
        </template>
        <template v-slot:cell-reason="{ row }">
          <input class="inline-reason" type="text" :value="row.reason || ''" @change="updateExclusionReason(row, $event.target.value)">
        </template>
        <template v-slot:cell-status="{ row }">
          <span class="status-pill" :class="row.is_active ? 'st-active' : 'st-muted'">
            {{ row.is_active ? 'Active' : 'Disabled' }}
          </span>
        </template>
        <template v-slot:cell-actions="{ row }">
          <AdminActionMenu>
            <button class="row-btn" @click="toggleExclusion(row)">
              {{ row.is_active ? 'Disable' : 'Activate' }}
            </button>
            <button class="row-btn danger" :disabled="!row.is_active" @click="deleteExclusion(row)">Delete</button>
          </AdminActionMenu>
        </template>
      </AdminTable>
    </section>
  </div>
</template>

<script>
import axios from 'axios'
import { apiDomain } from '../../config'
import AdminActionMenu from './shared/AdminActionMenu.vue'
import AdminTable from './shared/AdminTable.vue'

export default {
  name: 'AdminHome',
  components: { AdminActionMenu, AdminTable },
  data () {
    return {
      summary: {},
      dailySeries: [],
      dailyTotals: {},
      products: [],
      exclusions: [],
      sessions: [],
      catalogHealth: {},
      aiOverview: {},
      jobOverview: {
        running: 0,
        failed: 0,
        recent: 0
      },
      productDays: 30,
      rollupDays: 7,
      chartDays: 30,
      chartDayOptions: [1, 3, 7, 14, 30],
      loading: false,
      summaryLoading: true,
      dailyLoading: true,
      operationsLoading: true,
      trafficLoading: true,
      productsLoading: false,
      exclusionsLoading: false,
      rollupBusy: false,
      exclusionBusy: false,
      productsExpanded: false,
      error: '',
      notice: '',
      exclusionScopes: ['ip', 'email', 'username', 'user_id', 'visitor_id', 'session_id'],
      exclusionForm: {
        scope: 'ip',
        value: '',
        reason: ''
      }
    }
  },
  computed: {
    summaryCards () {
      const s = this.summary || {}
      const loading = this.summaryLoading && !Object.keys(s).length
      return [
        { key: 'sessions', label: 'Sessions', value: this.formatNumber(this.numOr(s, ['sessions'], 0)), note: `${this.formatNumber(this.numOr(s, ['active_sessions', 'activeSessions'], 0))} active now`, tone: 'blue', loading },
        { key: 'visitors', label: 'Unique visitors', value: this.formatNumber(this.numOr(s, ['unique_visitors', 'uniqueVisitors'], 0)), note: `${this.formatNumber(this.numOr(s, ['unique_users', 'uniqueUsers'], 0))} known users`, tone: 'default', loading },
        { key: 'registrations', label: 'Registrations', value: this.formatNumber(this.numOr(s, ['registrations'], 0)), note: 'New accounts in window', tone: 'green', loading },
        { key: 'amazon', label: 'Amazon clicks', value: this.formatNumber(this.numOr(s, ['amazon_clicks', 'amazonClicks'], 0)), note: `${this.formatNumber(this.numOr(s, ['product_clicks', 'productClicks'], 0))} product clicks`, tone: 'yellow', loading },
        { key: 'searches', label: 'Searches', value: this.formatNumber(this.numOr(s, ['searches'], 0)), note: `${this.formatNumber(this.numOr(s, ['page_views', 'pageViews'], 0))} page views`, tone: 'default', loading },
        { key: 'duration', label: 'Avg. duration', value: this.fmtDuration(this.numOr(s, ['avg_duration_seconds', 'avgDurationSeconds'], 0)), note: 'Average session length', tone: 'default', loading }
      ]
    },
    operationsCards () {
      const health = this.catalogHealth || {}
      const healthLoading = this.operationsLoading && !Object.keys(health).length
      const openFindings = this.catalogHealthCount(
        ['open_findings', 'openFindings', 'open', 'findings', 'total_open', 'totalOpen'],
        ['open_findings', 'openFindings', 'count', 'total']
      )
      const blockers = this.catalogHealthCount(
        ['blockers', 'open_blockers', 'openBlockers', 'blocker_count', 'blockerCount'],
        ['blockers', 'blocker', 'blocker_count', 'blockerCount']
      )
      const warnings = this.catalogHealthCount(
        ['warnings', 'open_warnings', 'openWarnings', 'warning_count', 'warningCount'],
        ['warnings', 'warning', 'warning_count', 'warningCount']
      )
      const aiTotals = (this.aiOverview && this.aiOverview.totals) || {}
      const aiCost = this.numOr(aiTotals, ['costUsd', 'cost_usd', 'totalCostUsd', 'total_cost_usd'], 0)
      const aiRateValue = this.pick(aiTotals, ['successRate', 'success_rate'], null)
      const aiRate = aiRateValue === null || aiRateValue === undefined || aiRateValue === '' ? null : Number(aiRateValue)
      const emptyPrompts = this.emptyPromptCount()
      const jobs = this.jobOverview || {}
      return [
        {
          key: 'part-issues',
          kicker: 'Catalog health',
          label: 'Open part issues',
          value: this.formatNumber(openFindings),
          note: `${this.formatNumber(blockers)} blockers / ${this.formatNumber(warnings)} warnings need review`,
          tone: blockers ? 'red' : warnings ? 'yellow' : 'green',
          loading: healthLoading
        },
        {
          key: 'ai-spend',
          kicker: 'AI operations',
          label: 'AI spend',
          value: this.fmtCost(aiCost),
          note: `${aiRate === null || isNaN(aiRate) ? 'No rate yet' : this.fmtPct(aiRate)} success${emptyPrompts ? ` / ${emptyPrompts} empty prompt${emptyPrompts === 1 ? '' : 's'}` : ''}`,
          tone: emptyPrompts ? 'yellow' : 'default',
          loading: this.operationsLoading && !Object.keys(this.aiOverview || {}).length
        },
        {
          key: 'jobs',
          kicker: 'Automation',
          label: 'Jobs',
          value: `${this.formatNumber(this.numOr(jobs, ['running'], 0))} running`,
          note: `${this.formatNumber(this.numOr(jobs, ['failed'], 0))} failed in last ${this.formatNumber(this.numOr(jobs, ['recent'], 0)) || 0} runs`,
          tone: this.numOr(jobs, ['failed'], 0) ? 'red' : 'green',
          loading: this.operationsLoading && !this.numOr(jobs, ['recent'], 0)
        }
      ]
    },
    productColumns () {
      return [
        { key: 'product', label: 'Product', width: '36%', breakable: true, sortable: true, defaultDir: 'asc', sortAccessor: (r) => this.productName(r) },
        { key: 'type', label: 'Type', width: '110px', nowrap: true, sortable: true, sortAccessor: (r) => this.partType(r) },
        { key: 'views', label: 'Views', width: '90px', nowrap: true, sortable: true, sortAccessor: (r) => Number(r.product_views || 0) },
        { key: 'actions', label: 'Builder actions', width: '160px', breakable: true },
        { key: 'amazon', label: 'Amazon', width: '90px', nowrap: true, sortable: true, sortAccessor: (r) => Number(r.amazon_clicks || 0) },
        { key: 'total', label: 'Total', width: '90px', nowrap: true, sortable: true, sortAccessor: (r) => Number(r.total_events || 0) }
      ]
    },
    visibleProducts () {
      return this.productsExpanded ? this.products : this.products.slice(0, 5)
    },
    exclusionColumns () {
      return [
        { key: 'scope', label: 'Scope', width: '110px', nowrap: true, sortable: true, sortAccessor: (r) => r.scope || '' },
        { key: 'value', label: 'Value', width: '24%', breakable: true, sortable: true, sortAccessor: (r) => r.value || '' },
        { key: 'reason', label: 'Reason', width: '34%', breakable: true },
        { key: 'status', label: 'Status', width: '110px', nowrap: true, sortable: true, sortAccessor: (r) => r.is_active ? 1 : 0 },
        { key: 'actions', label: '', width: '170px', align: 'right', nowrap: true }
      ]
    },
    topSources () {
      const summary = this.seriesFromSummary(['top_sources', 'topSources', 'sources'], 5)
      return summary.length ? summary : this.seriesFromSessions((session) => this.sourceLabel(session), 5)
    },
    topDevices () {
      const summary = this.seriesFromSummary(['devices', 'top_devices', 'topDevices'], 5)
      return summary.length ? summary : this.seriesFromSessions((session) => this.deviceTypeLabel(session), 5)
    },
    topPages () {
      const summary = this.seriesFromSummary(['top_pages', 'topPages', 'pages'], 6)
      return summary.length ? summary : this.seriesFromSessions((session) => this.pageLabel(session), 6)
    },
    chartGridLines () {
      return [24, 68, 112, 156, 200]
    },
    chartMax () {
      const values = []
      this.dailySeries.forEach((row) => {
        values.push(Number(row.sessions || 0))
        values.push(Number(row.unique_users || row.uniqueUsers || 0))
        values.push(Number(row.page_views || row.pageViews || 0))
        values.push(Number(row.amazon_clicks || row.amazonClicks || 0))
      })
      return Math.max.apply(null, values.concat([1]))
    },
    chartAxisTicks () {
      const rows = this.dailySeries || []
      if (!rows.length) return []
      if (rows.length === 1) {
        return [{ key: 'tick-0', label: this.chartDateLabel(rows[0]), left: 0 }]
      }
      const target = Math.min(6, rows.length)
      const seen = {}
      const ticks = []
      for (let i = 0; i < target; i += 1) {
        const index = Math.round((i * (rows.length - 1)) / (target - 1))
        if (seen[index]) continue
        seen[index] = true
        ticks.push({
          key: `tick-${index}`,
          label: this.chartDateLabel(rows[index]),
          left: Math.round((index / (rows.length - 1)) * 100)
        })
      }
      return ticks
    },
    chartHoverRects () {
      const rows = this.dailySeries || []
      if (!rows.length) return []
      const width = 640
      const band = rows.length > 1 ? width / (rows.length - 1) : width
      return rows.map((row, index) => {
        const center = rows.length > 1 ? index * band : width / 2
        const rectWidth = rows.length > 1 ? Math.max(18, band) : width
        return {
          key: `hit-${index}`,
          x: Math.max(0, center - rectWidth / 2),
          width: index === 0 || index === rows.length - 1 ? rectWidth / 2 : rectWidth,
          title: this.chartTooltip(row)
        }
      })
    }
  },
  mounted () {
    this.fetchAll()
  },
  methods: {
    authHeaders () {
      return {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${localStorage.usertoken}`
      }
    },
    async fetchAll () {
      this.loading = true
      this.notice = ''
      await Promise.all([
        this.fetchSummary(),
        this.fetchDaily(),
        this.fetchAudienceSamples(),
        this.fetchProducts(),
        this.fetchExclusions(),
        this.fetchOperations()
      ])
      this.loading = false
    },
    async fetchOperations () {
      this.operationsLoading = true
      try {
        await Promise.all([
          this.fetchCatalogHealth(),
          this.fetchAiOverview(),
          this.fetchJobOverview()
        ])
      } finally {
        this.operationsLoading = false
      }
    },
    async fetchCatalogHealth () {
      try {
        const params = new URLSearchParams({
          type: 'all',
          issue: 'all',
          severity: 'all',
          status: 'open'
        })
        const { data } = await axios.get(`${apiDomain}catalog-health/summary?${params.toString()}`, { headers: this.authHeaders() })
        this.catalogHealth = this.unwrap(data, ['summary', 'data']) || data || {}
      } catch (e) {
        this.catalogHealth = {}
      }
    },
    async fetchAiOverview () {
      try {
        const { data } = await axios.get(`${apiDomain}admin/ai-usage/summary?windowHours=720`, { headers: this.authHeaders() })
        this.aiOverview = data || {}
      } catch (e) {
        this.aiOverview = {}
      }
    },
    async fetchJobOverview () {
      try {
        const { data } = await axios.get(`${apiDomain}jobs/runs?limit=25`, { headers: this.authHeaders() })
        const runs = this.asList(data, ['runs', 'items', 'data'])
        this.jobOverview = {
          running: runs.filter((run) => this.jobStatus(run) === 'running').length,
          failed: runs.filter((run) => this.jobStatus(run) === 'failed').length,
          recent: runs.length
        }
      } catch (e) {
        this.jobOverview = { running: 0, failed: 0, recent: 0 }
      }
    },
    async fetchSummary () {
      this.summaryLoading = true
      try {
        const { data } = await axios.get(`${apiDomain}admin/audience/summary`, { headers: this.authHeaders() })
        this.summary = this.unwrap(data, ['summary', 'data']) || data || {}
        this.error = ''
      } catch (e) {
        this.error = this.errMsg(e)
      } finally {
        this.summaryLoading = false
      }
    },
    async fetchDaily () {
      this.dailyLoading = true
      try {
        const days = Math.max(1, Math.min(30, Number(this.chartDays) || 30))
        // Fetch one extra bucket because the API includes the current,
        // incomplete day. The chart should represent completed days only.
        const { data } = await axios.get(`${apiDomain}admin/audience/daily?days=${days + 1}`, { headers: this.authHeaders() })
        this.dailySeries = this.completedDailySeries(this.asList(data, ['series', 'data']), days)
        this.dailyTotals = this.unwrap(data, ['totals']) || {}
      } catch (e) {
        this.error = this.errMsg(e)
      } finally {
        this.dailyLoading = false
      }
    },
    completedDailySeries (series, limit) {
      const rows = Array.isArray(series) ? series.slice() : []
      if (!rows.length) return rows
      const last = rows[rows.length - 1]
      const date = String((last && (last.stat_date || last.statDate)) || '').slice(0, 10)
      const now = new Date()
      const today = [
        now.getFullYear(),
        String(now.getMonth() + 1).padStart(2, '0'),
        String(now.getDate()).padStart(2, '0')
      ].join('-')
      const metricKeys = ['sessions', 'unique_users', 'uniqueUsers', 'page_views', 'pageViews', 'amazon_clicks', 'amazonClicks', 'registrations']
      const emptyLatestBucket = metricKeys.every((key) => Number(last && last[key] || 0) === 0)
      if (date === today || emptyLatestBucket) rows.pop()
      return rows.slice(-Math.max(1, Number(limit) || 1))
    },
    async fetchAudienceSamples () {
      this.trafficLoading = true
      try {
        const params = new URLSearchParams({
          limit: '100',
          offset: '0'
        })
        const { data } = await axios.get(`${apiDomain}admin/audience/sessions?${params.toString()}`, { headers: this.authHeaders() })
        this.sessions = this.asList(data, ['sessions', 'items', 'data', 'results'])
      } catch (e) {
        this.sessions = []
      } finally {
        this.trafficLoading = false
      }
    },
    async fetchProducts () {
      this.productsLoading = true
      try {
        const { data } = await axios.get(`${apiDomain}admin/audience/products?days=${this.productDays}&limit=20`, { headers: this.authHeaders() })
        this.products = this.asList(data, ['products', 'items', 'data'])
      } catch (e) {
        this.error = this.errMsg(e)
      } finally {
        this.productsLoading = false
      }
    },
    async fetchExclusions () {
      this.exclusionsLoading = true
      try {
        const { data } = await axios.get(`${apiDomain}admin/audience/exclusions`, { headers: this.authHeaders() })
        this.exclusions = this.asList(data, ['exclusions', 'items', 'data'])
      } catch (e) {
        this.error = this.errMsg(e)
      } finally {
        this.exclusionsLoading = false
      }
    },
    async runRollup () {
      this.rollupBusy = true
      this.notice = ''
      try {
        const days = Math.max(1, Math.min(90, Number(this.rollupDays) || 7))
        const { data } = await axios.post(`${apiDomain}admin/audience/rollup`, { days }, { headers: this.authHeaders() })
        const rolled = this.pick(data, ['rolledUp', 'rolled_up'], 0)
        this.notice = `Analytics rollup rebuilt for ${rolled || days} day${(rolled || days) === 1 ? '' : 's'}.`
        await Promise.all([this.fetchSummary(), this.fetchDaily(), this.fetchAudienceSamples(), this.fetchProducts()])
      } catch (e) {
        this.error = this.errMsg(e)
      } finally {
        this.rollupBusy = false
      }
    },
    async createExclusion () {
      if (!this.exclusionForm.value) return
      this.exclusionBusy = true
      try {
        await axios.post(`${apiDomain}admin/audience/exclusions`, {
          scope: this.exclusionForm.scope,
          value: this.exclusionForm.value,
          reason: this.exclusionForm.reason
        }, { headers: this.authHeaders() })
        this.exclusionForm.value = ''
        this.exclusionForm.reason = ''
        this.notice = 'Exclusion added.'
        await this.fetchExclusions()
      } catch (e) {
        this.error = this.errMsg(e)
      } finally {
        this.exclusionBusy = false
      }
    },
    async updateExclusionReason (row, reason) {
      await this.updateExclusion(row, { reason, is_active: !!row.is_active })
    },
    async toggleExclusion (row) {
      await this.updateExclusion(row, { reason: row.reason || '', is_active: !row.is_active })
    },
    async updateExclusion (row, body) {
      if (!row || !row.id) return
      this.exclusionBusy = true
      try {
        await axios.put(`${apiDomain}admin/audience/exclusions/${encodeURIComponent(row.id)}`, body, { headers: this.authHeaders() })
        this.notice = 'Exclusion updated.'
        await this.fetchExclusions()
      } catch (e) {
        this.error = this.errMsg(e)
      } finally {
        this.exclusionBusy = false
      }
    },
    async deleteExclusion (row) {
      if (!row || !row.id) return
      this.exclusionBusy = true
      try {
        await axios.delete(`${apiDomain}admin/audience/exclusions/${encodeURIComponent(row.id)}`, { headers: this.authHeaders() })
        this.notice = 'Exclusion disabled.'
        await this.fetchExclusions()
      } catch (e) {
        this.error = this.errMsg(e)
      } finally {
        this.exclusionBusy = false
      }
    },
    chartPath (key) {
      const rows = this.dailySeries
      if (!rows.length) return ''
      const width = 640
      const height = 176
      const top = 22
      const step = rows.length > 1 ? width / (rows.length - 1) : width
      return rows.map((row, index) => {
        const raw = Number(row[key] || row[this.camelKey(key)] || 0)
        const x = Math.round(index * step)
        const y = Math.round(top + height - ((raw / this.chartMax) * height))
        return `${x},${y}`
      }).join(' ')
    },
    setChartDays (days) {
      const next = Number(days) || 30
      if (this.chartDays === next) return
      this.chartDays = next
      this.fetchDaily()
    },
    chartTooltip (row) {
      return [
        this.chartFullDateLabel(row),
        `Sessions: ${this.formatNumber(this.numOr(row, ['sessions'], 0))}`,
        `Active users: ${this.formatNumber(this.numOr(row, ['unique_users', 'uniqueUsers'], 0))}`,
        `Page views: ${this.formatNumber(this.numOr(row, ['page_views', 'pageViews'], 0))}`,
        `Amazon clicks: ${this.formatNumber(this.numOr(row, ['amazon_clicks', 'amazonClicks'], 0))}`,
        `Registrations: ${this.formatNumber(this.numOr(row, ['registrations'], 0))}`
      ].join('\n')
    },
    chartDateLabel (row) {
      const date = row && (row.stat_date || row.statDate)
      if (!date) return ''
      const d = new Date(date + 'T00:00:00')
      if (isNaN(d.getTime())) return date
      return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' })
    },
    chartFullDateLabel (row) {
      const date = row && (row.stat_date || row.statDate)
      if (!date) return ''
      const d = new Date(date + 'T00:00:00')
      if (isNaN(d.getTime())) return date
      return d.toLocaleDateString(undefined, { weekday: 'short', month: 'short', day: 'numeric', year: 'numeric' })
    },
    seriesFromSummary (keys, limit) {
      const raw = this.unwrap(this.summary || {}, keys) || []
      const list = Array.isArray(raw)
        ? raw
        : raw && typeof raw === 'object'
          ? Object.keys(raw).map((key) => ({ label: key, count: raw[key] }))
          : []
      const max = Math.max.apply(null, list.map((item) => Number(item.count || item.value_count || item.total || 0)).concat([1]))
      return list.slice(0, limit).map((item) => {
        const label = String(item.value || item.label || item.name || item.source || item.path || 'Unknown')
        const count = Number(item.count || item.value_count || item.total || 0)
        return {
          label,
          count,
          percent: Math.round((count / max) * 100)
        }
      })
    },
    seriesFromSessions (labelFn, limit) {
      const counts = {}
      const sessions = this.sessions || []
      sessions.forEach((session) => {
        const label = String(labelFn(session) || 'Unknown')
        counts[label] = (counts[label] || 0) + 1
      })
      const list = Object.keys(counts)
        .map((label) => ({ label, count: counts[label] }))
        .sort((a, b) => b.count - a.count || a.label.localeCompare(b.label))
        .slice(0, limit || 5)
      const max = Math.max.apply(null, list.map((item) => item.count).concat([1]))
      return list.map((item) => Object.assign({}, item, {
        percent: Math.max(4, Math.round((item.count / max) * 100))
      }))
    },
    sourceLabel (session) {
      const utm = this.pick(session, ['utm_source', 'utmSource'], '')
      if (utm) {
        const medium = this.pick(session, ['utm_medium', 'utmMedium'], '')
        return medium ? `${utm} / ${medium}` : utm
      }
      const direct = this.pick(session, ['source', 'host', 'domain'], '')
      if (direct) return direct
      const ref = this.pick(session, ['referrer', 'referer'], '')
      if (ref) {
        try { return new URL(ref).hostname } catch (e) { return ref }
      }
      return 'Direct'
    },
    deviceTypeLabel (session) {
      const raw = String(this.pick(session, ['device_type', 'deviceType', 'device'], '')).toLowerCase()
      if (raw.indexOf('mobile') >= 0 || raw.indexOf('phone') >= 0) return 'Mobile'
      if (raw.indexOf('tablet') >= 0) return 'Tablet'
      if (raw.indexOf('desktop') >= 0 || raw.indexOf('computer') >= 0) return 'Desktop'
      return raw ? this.prettyLabel(raw) : 'Other'
    },
    pageLabel (session) {
      return this.pick(session, ['landing_path', 'landingPath', 'entry_path', 'entryPath', 'path', 'last_event_path', 'lastEventPath'], 'Unknown')
    },
    catalogHealthCount (directKeys, categoryKeys) {
      const health = this.catalogHealth || {}
      const direct = this.pick(health, directKeys, null)
      if (direct !== null && direct !== undefined && direct !== '') {
        const n = Number(direct)
        return isNaN(n) ? 0 : n
      }
      const categories = this.catalogHealthCategories()
      if (!categories.length) return 0
      return categories.reduce((sum, item) => {
        const value = this.pick(item, categoryKeys, 0)
        const n = Number(value)
        return sum + (isNaN(n) ? 0 : n)
      }, 0)
    },
    catalogHealthCategories () {
      const health = this.catalogHealth || {}
      const raw = this.unwrap(health, ['categories', 'byType', 'by_type', 'types', 'partTypes'])
      if (Array.isArray(raw)) return raw
      if (raw && typeof raw === 'object') return Object.keys(raw).map((key) => {
        const value = raw[key]
        return value && typeof value === 'object' ? value : { type: key, count: value }
      })
      return []
    },
    productRowKey (row) {
      return row.target_id || row.asin || `${row.part_type || 'part'}:${row.part_id || row.targetId || ''}`
    },
    productName (row) {
      return row.model || row.name || row.asin || row.target_id || 'Unknown product'
    },
    productMeta (row) {
      return [row.asin, row.target_id || row.targetId].filter(Boolean).join(' / ') || '-'
    },
    partType (row) {
      return String(row.part_type || row.partType || 'part').toUpperCase()
    },
    camelKey (key) {
      return String(key).replace(/_([a-z])/g, (m, ch) => ch.toUpperCase())
    },
    num (value) {
      return Number(value || 0)
    },
    jobStatus (run) {
      return String((run && (run.status || run.state || run.resultStatus)) || '').toLowerCase()
    },
    emptyPromptCount () {
      const prompts = (this.aiOverview && this.aiOverview.byPrompt) || []
      return prompts.filter((row) => {
        const empty = this.pick(row, ['empty', 'emptyCalls', 'empty_calls', 'emptyResults', 'empty_results'], 0)
        if (Number(empty) > 0) return true
        const categories = row && (row.errorCategories || row.error_categories || row.errorsByCategory || row.errors_by_category)
        if (!categories) return false
        if (Array.isArray(categories)) {
          return categories.some((entry) => String(entry && (entry.key || entry.category || entry.error_category || entry.errorCategory)).toLowerCase().indexOf('empty') >= 0)
        }
        return Object.keys(categories).some((key) => key.toLowerCase().indexOf('empty') >= 0)
      }).length
    },
    formatNumber (value) {
      const n = Number(value || 0)
      return n.toLocaleString()
    },
    fmtCost (value) {
      const n = Number(value)
      if (isNaN(n)) return '$0.00'
      if (n === 0) return '$0.00'
      return n < 0.01 ? '$' + n.toFixed(6) : '$' + n.toFixed(2)
    },
    fmtPct (rate) {
      const n = Number(rate)
      if (isNaN(n)) return '0%'
      return Math.round(n * 100) + '%'
    },
    fmtDuration (seconds) {
      const total = Number(seconds || 0)
      if (!total) return '0s'
      const h = Math.floor(total / 3600)
      const m = Math.floor((total % 3600) / 60)
      const s = total % 60
      if (h) return `${h}h ${m}m`
      if (m) return `${m}m ${s ? s + 's' : ''}`.trim()
      return `${s}s`
    },
    prettyLabel (value) {
      return String(value || '')
        .replace(/[_-]+/g, ' ')
        .replace(/\b\w/g, (m) => m.toUpperCase())
    },
    asList (source, keys) {
      const value = this.unwrap(source, keys)
      if (Array.isArray(value)) return value
      return []
    },
    unwrap (source, keys) {
      if (!source) return null
      for (let i = 0; i < keys.length; i += 1) {
        const key = keys[i]
        if (source[key] !== undefined && source[key] !== null) return source[key]
      }
      return null
    },
    pick (source, keys, fallback) {
      if (!source) return fallback
      for (let i = 0; i < keys.length; i += 1) {
        const key = keys[i]
        if (source[key] !== undefined && source[key] !== null && source[key] !== '') return source[key]
      }
      return fallback
    },
    numOr (source, keys, fallback) {
      const value = this.pick(source, keys, fallback)
      const n = Number(value)
      return isNaN(n) ? fallback : n
    },
    errMsg (e) {
      const data = e && e.response && e.response.data
      return (data && (data.message || data.error && data.error.message || data.error)) ||
        e.message ||
        'Unable to load dashboard data.'
    }
  }
}
</script>

<style scoped>
.admin-home-page {
  padding: 4px 8px 44px;
  color: #cfd2da;
}

.label {
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  border-bottom: 1px solid rgba(67, 72, 87, 0.9);
}

.label h4 {
  margin: 0 0 6px;
  color: #d8dde8;
  font-size: 18px;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
}

.eyebrow,
.panel-kicker {
  display: block;
  margin-bottom: 3px;
  color: #1997c6;
  font-size: 11px;
  font-weight: 800;
  letter-spacing: 0.1em;
  text-transform: uppercase;
}

.intro {
  max-width: 880px;
  margin: 0.6rem 0 1.1rem;
  color: #a4acbc;
  font-size: 14px;
  line-height: 1.55;
}

.hero-actions,
.row-actions,
.rollup-actions {
  display: flex;
  align-items: center;
  gap: 10px;
}

.alert,
.notice {
  margin: 12px 0;
  padding: 10px 12px;
  border-radius: 8px;
  font-size: 13px;
}

.alert {
  border: 1px solid rgba(232, 79, 106, 0.5);
  background: rgba(90, 34, 48, 0.48);
  color: #f3c6cf;
}

.notice {
  border: 1px solid rgba(27, 201, 142, 0.42);
  background: rgba(27, 201, 142, 0.1);
  color: #a8efd0;
}

.kpi-grid {
  display: grid;
  grid-template-columns: repeat(6, minmax(140px, 1fr));
  gap: 12px;
  margin: 16px 0;
}

.kpi-card,
.panel {
  border: 1px solid rgba(67, 72, 87, 0.92);
  border-radius: 8px;
  background:
    radial-gradient(circle at 12% 0%, rgba(25, 151, 198, 0.06), transparent 36%),
    rgba(10, 14, 22, 0.68);
  box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.035);
}

.kpi-card {
  display: grid;
  gap: 6px;
  min-height: 108px;
  padding: 14px 15px;
}

.kpi-label {
  color: #8f98aa;
  font-size: 11px;
  font-weight: 800;
  letter-spacing: 0.08em;
  text-transform: uppercase;
}

.kpi-card strong {
  color: #e5e9f1;
  font-size: 30px;
  line-height: 1;
}

.kpi-card small {
  color: #8f98aa;
  font-size: 12px;
}

.kpi-card.is-loading strong,
.op-card.is-loading strong {
  color: #9ea8ba;
  letter-spacing: 0.05em;
}

.kpi-card.is-loading small,
.op-card.is-loading small {
  color: #6f788b;
}

.kpi-card.tone-blue { border-color: rgba(25, 151, 198, 0.56); }
.kpi-card.tone-green { border-color: rgba(27, 201, 142, 0.42); }
.kpi-card.tone-yellow { border-color: rgba(224, 192, 126, 0.38); }

.ops-grid {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 12px;
  margin: 0 0 16px;
}

.op-card {
  display: grid;
  gap: 10px;
  min-height: 118px;
  padding: 14px 15px;
  border: 1px solid rgba(67, 72, 87, 0.86);
  border-radius: 8px;
  background:
    radial-gradient(circle at 16% 0%, rgba(25, 151, 198, 0.07), transparent 40%),
    rgba(9, 13, 21, 0.72);
  box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.03);
}

.op-card h5 {
  margin: 0;
  color: #dce1eb;
  font-size: 15px;
  font-weight: 800;
}

.op-card strong {
  color: #e5e9f1;
  font-size: 26px;
  line-height: 1;
}

.op-card small {
  color: #8f98aa;
  font-size: 12px;
  line-height: 1.45;
}

.op-card.tone-blue { border-color: rgba(25, 151, 198, 0.5); }
.op-card.tone-green { border-color: rgba(27, 201, 142, 0.42); }
.op-card.tone-yellow { border-color: rgba(224, 192, 126, 0.38); }
.op-card.tone-red { border-color: rgba(232, 79, 106, 0.42); }

.dashboard-grid {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 14px;
}

.panel {
  padding: 15px;
}

.panel-wide {
  grid-column: span 3;
  min-height: 322px;
}

.rollup-panel {
  grid-column: span 1;
}

.panel-headline {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 12px;
}

.panel-headline h5 {
  margin: 0;
  color: #dce1eb;
  font-size: 17px;
  font-weight: 800;
}

.panel-note,
.rollup-panel p {
  color: #8f98aa;
  font-size: 12px;
  line-height: 1.45;
}

.product-head-actions {
  display: flex;
  align-items: center;
  gap: 10px;
}

.chart-legend {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  color: #8f98aa;
  font-size: 11px;
  font-weight: 800;
}

.chart-head-tools {
  display: grid;
  justify-items: end;
  gap: 9px;
}

.chart-window-toggle {
  display: inline-flex;
  padding: 3px;
  border: 1px solid rgba(67, 72, 87, 0.78);
  border-radius: 999px;
  background: rgba(8, 11, 17, 0.58);
}

.chart-window-toggle button {
  min-width: 34px;
  height: 24px;
  border: 0;
  border-radius: 999px;
  background: transparent;
  color: #8f98aa;
  cursor: pointer;
  font-size: 11px;
  font-weight: 900;
}

.chart-window-toggle button.active {
  background: rgba(25, 151, 198, 0.2);
  color: #45c9f7;
  box-shadow: inset 0 0 0 1px rgba(32, 184, 235, 0.38);
}

.chart-window-toggle button:disabled {
  cursor: default;
  opacity: 0.7;
}

.chart-legend span {
  display: inline-flex;
  align-items: center;
  gap: 5px;
}

.chart-legend i {
  width: 18px;
  height: 3px;
  border-radius: 999px;
}

.line-sessions { background: #1fb2e8; }
.line-users { background: #aab5ff; }
.line-views { background: #8fe8c4; }
.line-amazon { background: #e0c07e; }

.line-chart-wrap {
  height: 246px;
}

.chart-loading {
  min-height: 246px;
  display: grid;
  place-items: center;
}

.line-chart {
  width: 100%;
  height: 220px;
}

.line-chart line {
  stroke: rgba(67, 72, 87, 0.38);
  stroke-width: 1;
}

.chart-line {
  fill: none;
  stroke-linecap: round;
  stroke-linejoin: round;
  stroke-width: 3;
  vector-effect: non-scaling-stroke;
}

.chart-line-sessions { stroke: #1fb2e8; }
.chart-line-users { stroke: #aab5ff; opacity: 0.92; }
.chart-line-views { stroke: #8fe8c4; opacity: 0.88; }
.chart-line-amazon { stroke: #e0c07e; opacity: 0.9; }

.chart-hit {
  fill: transparent;
  cursor: crosshair;
}

.chart-axis {
  position: relative;
  height: 18px;
  color: #6f788b;
  font-size: 11px;
}

.chart-axis span {
  position: absolute;
  top: 0;
  transform: translateX(-50%);
  white-space: nowrap;
}

.chart-axis span:first-child {
  transform: translateX(0);
}

.chart-axis span:last-child {
  transform: translateX(-100%);
}

.bar-list,
.rank-list {
  display: grid;
  gap: 10px;
}

.bar-row {
  display: grid;
  grid-template-columns: minmax(70px, 1fr) 1.5fr auto;
  align-items: center;
  gap: 9px;
  color: #cfd2da;
  font-size: 12px;
}

.bar-row span {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.bar-row div {
  height: 7px;
  overflow: hidden;
  border-radius: 999px;
  background: rgba(67, 72, 87, 0.58);
}

.bar-row i {
  display: block;
  height: 100%;
  border-radius: inherit;
  background: linear-gradient(90deg, #1fb2e8, #8fe8c4);
}

.rank-row {
  display: flex;
  justify-content: space-between;
  gap: 10px;
  padding-bottom: 9px;
  border-bottom: 1px solid rgba(67, 72, 87, 0.36);
  color: #cfd2da;
  font-size: 12px;
}

.rank-row span {
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.mini-empty,
.empty-panel {
  color: #6f788b;
  font-size: 12px;
}

.rollup-actions {
  align-items: flex-end;
  margin-top: 14px;
}

.rollup-actions label {
  display: grid;
  gap: 5px;
  margin: 0;
  color: #8f98aa;
  font-size: 11px;
  font-weight: 800;
  text-transform: uppercase;
}

.rollup-actions input,
.exclusion-form input,
.exclusion-form select,
.inline-reason {
  height: 34px;
  border: 1px solid rgba(67, 72, 87, 0.95);
  border-radius: 6px;
  background: rgba(8, 11, 17, 0.62);
  color: #d7dce8;
  padding: 0 10px;
  outline: none;
}

.rollup-actions input {
  width: 80px;
}

.product-panel,
.exclusions-panel {
  margin-top: 14px;
}

.product-name {
  display: block;
  overflow: hidden;
  color: #e2e7ef;
  font-weight: 800;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.muted-sub {
  display: block;
  margin-top: 3px;
  color: #7f899b;
  font-size: 11px;
}

.mono {
  font-family: Consolas, 'Courier New', monospace;
}

.type-pill,
.status-pill {
  display: inline-flex;
  align-items: center;
  height: 22px;
  padding: 0 8px;
  border: 1px solid rgba(25, 151, 198, 0.36);
  border-radius: 999px;
  background: rgba(25, 151, 198, 0.1);
  color: #9adef5;
  font-size: 10px;
  font-weight: 900;
  letter-spacing: 0.06em;
  text-transform: uppercase;
}

.status-pill.st-active {
  border-color: rgba(27, 201, 142, 0.36);
  background: rgba(27, 201, 142, 0.1);
  color: #8fe8c4;
}

.status-pill.st-muted {
  border-color: rgba(117, 124, 146, 0.28);
  background: rgba(58, 63, 77, 0.34);
  color: #9aa3b4;
}

.amazon-count {
  color: #e4d836;
}

.event-stack {
  display: grid;
  gap: 2px;
}

.event-stack small {
  color: #7f899b;
  font-size: 11px;
}

.exclusion-form {
  display: grid;
  grid-template-columns: 150px 1fr 1.3fr auto;
  gap: 10px;
  margin-bottom: 14px;
}

.inline-reason {
  width: 100%;
}

.text-cell {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.btn-refresh,
.btn-secondary,
.row-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-height: 34px;
  border: 1px solid rgba(25, 151, 198, 0.8);
  border-radius: 6px;
  background: rgba(25, 151, 198, 0.1);
  color: #1fb2e8;
  cursor: pointer;
  font-size: 12px;
  font-weight: 800;
  text-decoration: none;
  text-transform: uppercase;
  transition: border-color 0.18s ease, background 0.18s ease, color 0.18s ease;
}

.btn-refresh,
.btn-secondary {
  padding: 0 15px;
}

.btn-secondary.compact {
  min-height: 28px;
  padding: 0 10px;
  font-size: 10.5px;
}

.btn-refresh:hover:not(:disabled),
.btn-secondary:hover,
.row-btn:hover:not(:disabled) {
  border-color: #1fb2e8;
  background: rgba(25, 151, 198, 0.2);
  color: #eaf7fc;
}

.btn-refresh:disabled,
.row-btn:disabled {
  opacity: 0.5;
  cursor: default;
}

.row-btn {
  padding: 5px 10px;
}

.row-btn.danger {
  border-color: rgba(232, 79, 106, 0.58);
  color: #f0a1af;
}

@media screen and (max-width: 1220px) {
  .kpi-grid {
    grid-template-columns: repeat(3, minmax(160px, 1fr));
  }

  .ops-grid {
    grid-template-columns: repeat(3, minmax(0, 1fr));
  }

  .dashboard-grid {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  .panel-wide {
    grid-column: span 2;
  }
}

@media screen and (max-width: 940px) {
  .ops-grid {
    grid-template-columns: 1fr;
  }

  .panel-headline {
    flex-direction: column;
  }

  .chart-head-tools {
    justify-items: start;
  }
}

@media screen and (max-width: 760px) {
  .label,
  .hero-actions,
  .jobs-category-head {
    align-items: stretch;
    flex-direction: column;
  }

  .kpi-grid,
  .ops-grid,
  .dashboard-grid,
  .exclusion-form {
    grid-template-columns: 1fr;
  }

  .panel-wide,
  .rollup-panel {
    grid-column: span 1;
  }

  .hero-actions,
  .product-head-actions,
  .rollup-actions,
  .row-actions {
    flex-wrap: wrap;
  }
}
</style>
